import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import DocumentModal from "./modals/DocumentsModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingOverlay from "./LoadingOverlay";
import { TbDownload, TbTrashX, TbFileText, TbSearch } from "react-icons/tb";

/* ── per-docType colour themes ───────────────────────────────────── */
const THEME = {
  msds: {
    header: "bg-teal-600",
    tableHead: "bg-teal-700",
    tabActive: "bg-teal-600 text-white",
    ring: "focus:ring-teal-400",
    btn: "bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-600 hover:text-white",
    upload: "bg-teal-600 hover:bg-teal-700",
    rowHover: "hover:bg-teal-50",
    border: "hover:border-teal-400",
    tabHover: "hover:text-teal-600 hover:bg-teal-50",
  },
  icp: {
    header: "bg-indigo-600",
    tableHead: "bg-indigo-700",
    tabActive: "bg-indigo-600 text-white",
    ring: "focus:ring-indigo-400",
    btn: "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-600 hover:text-white",
    upload: "bg-indigo-600 hover:bg-indigo-700",
    rowHover: "hover:bg-indigo-50",
    border: "hover:border-indigo-400",
    tabHover: "hover:text-indigo-600 hover:bg-indigo-50",
  },
  spec: {
    header: "bg-violet-600",
    tableHead: "bg-violet-700",
    tabActive: "bg-violet-600 text-white",
    ring: "focus:ring-violet-400",
    btn: "bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-600 hover:text-white",
    upload: "bg-violet-600 hover:bg-violet-700",
    rowHover: "hover:bg-violet-50",
    border: "hover:border-violet-400",
    tabHover: "hover:text-violet-600 hover:bg-violet-50",
  },
};

const EXPIRY_DAYS = { msds: 5 * 365, icp: 1 * 365 };

function isExpired(issueDate, docType) {
  const days = EXPIRY_DAYS[docType];
  if (!days) return null; // spec → n/a
  return Date.now() - new Date(issueDate) > days * 24 * 60 * 60 * 1000;
}

function ExpiryBadge({ issueDate, docType }) {
  const expired = isExpired(issueDate, docType);
  if (expired === null)
    return <span className="text-gray-400 text-xs">n/a</span>;
  return expired ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      Expired
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      Valid
    </span>
  );
}

const FILTER_TABS = ["All", "Valid", "Expired"];

function Documents({ docType }) {
  const t = THEME[docType] || THEME.msds;
  const { user, permissions } = useSelector((state) => state.auth);
  const canDelete =
    user?.user_lvl === 1 ||
    permissions === "admin" ||
    (Array.isArray(permissions) && permissions.includes("docs.delete"));

  const [fileName, setFileName] = useState("");
  const [msdsList, setMsdsList] = useState([]);
  const [file, setFile] = useState(null);
  const [fileLabel, setFileLabel] = useState("No file chosen");
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [issueDate, setIssueDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTab, setFilterTab] = useState(0); // 0=All 1=Valid 2=Expired
  const [downloadingId, setDownloadingId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getDocsList();
  }, []);

  const getDocsList = async () => {
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/${docType}`,
      );
      setMsdsList(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to load documents.");
    }
  };

  /* filtered + searched list */
  const filtered = useMemo(() => {
    let list = msdsList;
    // expiry tab
    if (filterTab === 1)
      list = list.filter((d) => isExpired(d.issue_date, docType) === false);
    if (filterTab === 2)
      list = list.filter((d) => isExpired(d.issue_date, docType) === true);
    // search
    const q = search.trim().toLowerCase();
    if (q)
      list = list.filter((d) =>
        d.filename.replace(/_@\d+/, "").toLowerCase().includes(q),
      );
    return list;
  }, [msdsList, filterTab, search, docType]);

  const tabCounts = useMemo(
    () => ({
      all: msdsList.length,
      valid: msdsList.filter((d) => isExpired(d.issue_date, docType) === false)
        .length,
      expired: msdsList.filter((d) => isExpired(d.issue_date, docType) === true)
        .length,
    }),
    [msdsList, docType],
  );

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setFileLabel(f.name);
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file.");
      return;
    }
    if (!fileName) {
      toast.error("Please enter a name.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  const executeUpload = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      const ext = file.name.split(".").pop();
      formData.append("file", file);
      formData.append("customFileName", `${fileName}_@${Date.now()}.${ext}`);
      formData.append("issue_date", format(issueDate, "yyyy-MM-dd"));
      await axios.post(
        `https://eps.elkomprintama.com/${docType}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      toast.success("File uploaded successfully.");
      getDocsList();
      setFileName("");
      setFile(null);
      setFileLabel("No file chosen");
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsLoading(false);
      setUploadPending(false);
    }
  };

  const handleDownload = async (id, customPath, originalName) => {
    setDownloadingId(id);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/docs/download",
        {
          params: { customPath },
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", originalName.replace(/_@\d+/g, ""));
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsLoading(true);
    try {
      await axios.delete(
        `https://eps.elkomprintama.com/delete/api/docs/${deleteTarget.id}`,
      );
      toast.success("File deleted.");
      getDocsList();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Delete failed.");
    } finally {
      setIsLoading(false);
      setDeleteTarget(null);
    }
  };

  const cleanName = (filename) =>
    filename.replace(/_@\d+\.[^.]+$/, "").replace(/_@\d+/, "");

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Processing..." />}

      {/* Header */}
      <div className={`w-full rounded-lg ${t.header} mb-4 flex-shrink-0`}>
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          {docType.toUpperCase()}
        </label>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Upload panel */}
        <div className="w-72 flex-shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-700">Upload Document</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Add a new {docType.toUpperCase()} file
            </p>
          </div>

          <form
            onSubmit={handleUpload}
            className="px-5 py-4 flex flex-col gap-4 flex-1"
          >
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Material Name
              </label>
              <input
                className={`w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 ${t.ring}`}
                type="text"
                placeholder="e.g. PET Film"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                File
              </label>
              <div
                className={`w-full border-2 border-dashed border-gray-300 rounded-lg px-3 py-4 text-center cursor-pointer transition ${t.border}`}
                onClick={() => fileRef.current?.click()}
              >
                <TbFileText className="mx-auto mb-1 text-gray-300" size={28} />
                <p className="text-xs text-gray-400 truncate px-2">
                  {fileLabel}
                </p>
                <p
                  className={`text-xs font-semibold mt-1 ${t.header.replace("bg-", "text-")}`}
                >
                  Click to browse
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Issue Date
              </label>
              <DatePicker
                selected={issueDate}
                onChange={setIssueDate}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 ${t.ring}`}
                wrapperClassName="w-full"
              />
              <p className="text-xs text-gray-400 mt-1">
                {docType === "msds" && "⏱ MSDS expires 5 years from issue date"}
                {docType === "icp" && "⏱ ICP expires 1 year from issue date"}
                {docType === "spec" && "✓ Spec documents do not expire"}
              </p>
            </div>

            <button
              type="submit"
              className={`mt-auto w-full py-2.5 rounded-xl text-sm font-bold text-white transition ${t.upload}`}
            >
              Upload
            </button>
          </form>
        </div>

        {/* Document list */}
        <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Tabs + search bar */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
            {/* Tab row */}
            <div className="flex px-4 pt-3 gap-1">
              {FILTER_TABS.map((tab, i) => {
                const count = [
                  tabCounts.all,
                  tabCounts.valid,
                  tabCounts.expired,
                ][i];
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFilterTab(i)}
                    className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none ${
                      filterTab === i
                        ? t.tabActive
                        : `text-gray-500 ${t.tabHover}`
                    }`}
                  >
                    {tab}
                    <span
                      className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${
                        filterTab === i
                          ? "bg-white/30"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search row */}
            <div className="px-4 pb-3 pt-2">
              <div className="relative">
                <TbSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search material name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`w-full border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 ${t.ring}`}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 min-h-0 overflow-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-2">
                <TbFileText size={40} />
                <p className="text-sm">
                  {search
                    ? "No matching documents."
                    : "No documents uploaded yet."}
                </p>
              </div>
            ) : (
              <table className="text-xs w-full border-collapse">
                <thead
                  className={`sticky top-0 z-10 text-white uppercase ${t.tableHead}`}
                >
                  <tr>
                    <th className="px-4 py-3 border-r text-left w-10">#</th>
                    <th className="px-4 py-3 border-r text-left">
                      Material Name
                    </th>
                    <th className="px-4 py-3 border-r text-center">Size</th>
                    <th className="px-4 py-3 border-r text-center">
                      Issue Date
                    </th>
                    <th className="px-4 py-3 border-r text-center">Status</th>
                    <th className="px-4 py-3 border-r text-center">Download</th>
                    {canDelete && (
                      <th className="px-4 py-3 text-center">Delete</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((doc, i) => (
                    <tr
                      key={doc.id}
                      className={`border-b cursor-pointer transition ${i % 2 === 0 ? "bg-gray-50" : "bg-white"} ${t.rowHover}`}
                      onDoubleClick={() => {
                        setModalData(doc);
                        setShowModal(true);
                      }}
                    >
                      <td className="px-4 py-3 border-r text-gray-400 text-center">
                        {i + 1}
                      </td>
                      <td className="px-4 py-3 border-r font-medium text-gray-700 whitespace-nowrap">
                        {cleanName(doc.filename)}
                      </td>
                      <td className="px-4 py-3 border-r text-center text-gray-500">
                        {(doc.size / 1000).toFixed(1)} KB
                      </td>
                      <td className="px-4 py-3 border-r text-center text-gray-600 whitespace-nowrap">
                        {new Date(doc.issue_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 border-r text-center">
                        <ExpiryBadge
                          issueDate={doc.issue_date}
                          docType={docType}
                        />
                      </td>
                      <td className="px-4 py-3 border-r text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(doc.id, doc.path, doc.filename);
                          }}
                          disabled={downloadingId === doc.id}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition ${t.btn} disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          {downloadingId === doc.id ? (
                            <>
                              <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                              </svg>
                              Downloading…
                            </>
                          ) : (
                            <><TbDownload size={13} /> Download</>
                          )}
                        </button>
                      </td>
                      {canDelete && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(doc);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-600 hover:text-white transition"
                          >
                            <TbTrashX size={13} /> Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Result count footer */}
          {search && (
            <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "
              {search}"
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {deleteTarget && (
        <ConfirmDialog
          message={`Delete "${cleanName(deleteTarget.filename)}"? This cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Upload this file?"
          onConfirm={() => {
            setShowConfirm(false);
            executeUpload();
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <DocumentModal
            dataSet={modalData}
            onClose={() => setShowModal(false)}
            refreshData={getDocsList}
          />
        </div>
      )}
    </div>
  );
}

export default Documents;
